import React, { useState, useEffect, useRef } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { useAuth } from '../../context/AuthContext';
import { WhatsAppSocketProvider, useWhatsAppSocket } from '../../context/WhatsAppSocketContext';
import { 
  Settings, Send, Check, CheckCheck, ShieldAlert, KeyRound, Phone, 
  User as UserIcon, MessageCircle, Trash2, UserCheck, RefreshCw, 
  History, Clock, ArrowRightLeft, UserX, CheckSquare, Plus, FileText, PlusCircle, Paperclip, X, Image as ImageIcon
} from 'lucide-react';
import QuickReplies from '../../components/whatsapp/QuickReplies';
import { LibrarySendButton } from '../../components/whatsapp/MediaSendButton';
import MediaPickerModal from '../../components/shared/MediaPickerModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const WhatsAppPageContent = () => {
  const { user } = useAuth();
  const { socket, isConnected: socketConnected } = useWhatsAppSocket();
  
  const [config, setConfig] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [logs, setLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  const [activeTab, setActiveTab] = useState('unassigned'); // 'unassigned' | 'mine' | 'all' | 'templates'
  const [newMessageText, setNewMessageText] = useState('');
  
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  
  // Settings Form state
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [verifyToken, setVerifyToken] = useState('crmautomax_verify_secret_token_2026');
  const [displayName, setDisplayName] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);

  // New state variables for WhatsApp Templates
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState('');
  const [newChatSelectedTemplate, setNewChatSelectedTemplate] = useState(null);
  const [newChatTemplateVariables, setNewChatTemplateVariables] = useState({});
  const [newChatHeaderVariables, setNewChatHeaderVariables] = useState({});
  const [newChatHeaderMediaUrl, setNewChatHeaderMediaUrl] = useState('');
  const [newChatBodyVariables, setNewChatBodyVariables] = useState({});
  const [newChatButtonVariables, setNewChatButtonVariables] = useState({});
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [createTemplateName, setCreateTemplateName] = useState('');
  const [createTemplateCategory, setCreateTemplateCategory] = useState('UTILITY');
  const [createTemplateLanguage, setCreateTemplateLanguage] = useState('en_US');
  const [createTemplateBody, setCreateTemplateBody] = useState('');
  const [createTemplateHeader, setCreateTemplateHeader] = useState('');
  const [createTemplateFooter, setCreateTemplateFooter] = useState('');
  const [createTemplateSaving, setCreateTemplateSaving] = useState(false);
  const [createTemplateError, setCreateTemplateError] = useState('');
  const [sendingTemplate, setSendingTemplate] = useState(false);

  // New state variables for Permanent Contact Name System
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  
  const [showLinkLeadModal, setShowLinkLeadModal] = useState(false);
  const [leadSearchQuery, setLeadSearchQuery] = useState('');
  const [leadSearchResults, setLeadSearchResults] = useState([]);
  const [searchingLeads, setSearchingLeads] = useState(false);
  const [linkingLead, setLinkingLead] = useState(false);

  const messagesEndRef = useRef(null);

  // Helper parser for template body and header text placeholders
  const getTemplatePlaceholders = (template) => {
    if (!template) return [];
    const placeholders = [];
    
    // Scan Header text
    const headerComp = template.components?.find(c => c.type?.toUpperCase() === 'HEADER');
    if (headerComp && headerComp.text) {
      const headerMatches = [...headerComp.text.matchAll(/\{\{([^}]+)\}\}/g)];
      headerMatches.forEach(m => placeholders.push(m[1]));
    }
    
    // Scan Body text
    const bodyComp = template.components?.find(c => c.type?.toUpperCase() === 'BODY');
    if (bodyComp && bodyComp.text) {
      const bodyMatches = [...bodyComp.text.matchAll(/\{\{([^}]+)\}\}/g)];
      bodyMatches.forEach(m => placeholders.push(m[1]));
    }
    
    return [...new Set(placeholders)].sort((a, b) => {
      const numA = parseInt(a);
      const numB = parseInt(b);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.localeCompare(b);
    });
  };

  // Helper parser for header-only placeholders
  const getHeaderPlaceholders = (template) => {
    if (!template) return [];
    const placeholders = [];
    const headerComp = template.components?.find(c => c.type?.toUpperCase() === 'HEADER');
    if (headerComp && headerComp.text) {
      const headerMatches = [...headerComp.text.matchAll(/\{\{([^}]+)\}\}/g)];
      headerMatches.forEach(m => placeholders.push(m[1]));
    }
    return [...new Set(placeholders)].sort((a, b) => {
      const numA = parseInt(a);
      const numB = parseInt(b);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.localeCompare(b);
    });
  };

  // Helper parser for body-only placeholders
  const getBodyPlaceholders = (template) => {
    if (!template) return [];
    const placeholders = [];
    const bodyComp = template.components?.find(c => c.type?.toUpperCase() === 'BODY');
    if (bodyComp && bodyComp.text) {
      const bodyMatches = [...bodyComp.text.matchAll(/\{\{([^}]+)\}\}/g)];
      bodyMatches.forEach(m => placeholders.push(m[1]));
    }
    return [...new Set(placeholders)].sort((a, b) => {
      const numA = parseInt(a);
      const numB = parseInt(b);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.localeCompare(b);
    });
  };

  // Helper parser for template dynamic URL button placeholders
  const getTemplateButtonPlaceholders = (template) => {
    if (!template) return [];
    const buttonsComp = template.components?.find(c => c.type?.toUpperCase() === 'BUTTONS');
    if (!buttonsComp || !buttonsComp.buttons) return [];

    const result = [];
    buttonsComp.buttons.forEach((btn, index) => {
      if (btn.type?.toUpperCase() === 'URL' && btn.url && btn.url.includes('{{1}}')) {
        result.push({
          index: index.toString(),
          text: btn.text,
          url: btn.url
        });
      }
    });
    return result;
  };

  // Fetch templates from Meta API
  const fetchTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      } else {
        const errData = await res.json();
        console.error('Failed to fetch templates:', errData.message);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // 1. Fetch Config
  const fetchConfig = async () => {
    try {
      setLoadingConfig(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        if (data.isConnected) {
          setPhoneNumberId(data.phoneNumberId || '');
          setDisplayName(data.displayName || '');
          setWabaId(data.wabaId || '');
          setVerifyToken(data.verifyToken || 'crmautomax_verify_secret_token_2026');
          fetchContacts();
          fetchEmployees();
          if (data.wabaId) {
            // Also fetch templates if WABA ID is configured
            try {
              const resTmpl = await fetch(`${API_BASE}/whatsapp/templates`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (resTmpl.ok) {
                const dataTmpl = await resTmpl.json();
                setTemplates(dataTmpl);
              }
            } catch (tErr) {
              console.error('Error fetching templates during config load:', tErr);
            }
          }
        } else {
          setShowSettingsModal(true);
        }
      }
    } catch (err) {
      console.error('Error fetching WhatsApp config:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  // 2. Fetch Contacts based on activeTab
  const fetchContacts = async () => {
    if (activeTab === 'templates') return;
    try {
      setLoadingContacts(true);
      const token = localStorage.getItem('crm_token');
      let endpoint = '/whatsapp/contacts/unassigned';
      if (activeTab === 'mine') endpoint = '/whatsapp/contacts/mine';
      if (activeTab === 'all') endpoint = '/whatsapp/contacts/all';

      const res = await fetch(`${API_BASE}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setContacts(data);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  // 3. Fetch Employees for reassignment dropdown
  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data.filter(u => u.role !== 'master-admin'));
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  // 4. Fetch Conversation messages
  const fetchMessages = async (phone) => {
    try {
      setLoadingMessages(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/messages/${phone}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
        
        // Zero out unread count in UI contact list instantly
        setContacts(prev =>
          prev.map(c => (c.phone === phone ? { ...c, unreadCount: 0 } : c))
        );
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  // 5. Fetch Activity Logs
  const fetchLogs = async (contactId) => {
    try {
      setLoadingLogs(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/contacts/${contactId}/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data);
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setLoadingLogs(false);
    }
  };

  // 6. Accept unassigned chat
  const handleAcceptChat = async (contactId) => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/contacts/${contactId}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        fetchContacts();
        if (activeContact && activeContact._id === contactId) {
          setActiveContact(data.contact);
          fetchLogs(contactId);
        }
      }
    } catch (err) {
      console.error('Error accepting chat:', err);
    }
  };

  // 7. Assign / Re-assign chat to employee
  const handleAssignChat = async (contactId, targetUserId) => {
    if (!targetUserId) return;
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/contacts/${contactId}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ targetUserId })
      });
      if (res.ok) {
        const data = await res.json();
        fetchContacts();
        if (activeContact && activeContact._id === contactId) {
          setActiveContact(data.contact);
          fetchLogs(contactId);
        }
      } else {
        const errData = await res.json();
        alert(errData.message || 'Failed to assign chat');
      }
    } catch (err) {
      console.error('Error assigning chat:', err);
    }
  };

  // 8. Permanent delete chat
  const handleDeleteChat = async (contactId) => {
    if (!window.confirm('Are you sure you want to permanently delete this chat? All messages and activity logs will be deleted.')) return;
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/contacts/${contactId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setActiveContact(null);
        setMessages([]);
        setLogs([]);
        fetchContacts();
      } else {
        const errData = await res.json();
        alert(errData.message || 'Failed to delete chat');
      }
    } catch (err) {
      console.error('Error deleting chat:', err);
    }
  };

  // 9. Save Config
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    if (!phoneNumberId || !accessToken || !verifyToken) {
      setSettingsError('Please fill in Phone Number ID, Access Token, and Verification Token.');
      return;
    }

    try {
      setSettingsSaving(true);
      setSettingsError('');
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          phoneNumberId,
          accessToken,
          verifyToken,
          displayName: displayName || 'WhatsApp Business API',
          wabaId
        })
      });

      const data = await res.json();
      if (res.ok) {
        setConfig(data.config);
        setShowSettingsModal(false);
        fetchContacts();
      } else {
        setSettingsError(data.message || 'Failed to save configuration');
      }
    } catch (err) {
      setSettingsError('Server connection failed. Try again.');
      console.error(err);
    } finally {
      setSettingsSaving(false);
    }
  };

  // 10. Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessageText.trim() && !selectedMedia) || !activeContact || sendingMessage) return;

    const to = activeContact.phone;
    const body = newMessageText.trim();

    try {
      setSendingMessage(true);
      const token = localStorage.getItem('crm_token');
      let res;
      
      if (selectedMedia) {
        const formData = new FormData();
        formData.append('to', to);
        if (body) formData.append('caption', body);
        
        if (selectedMedia.fileUrl) {
          formData.append('mediaUrl', selectedMedia.fileUrl);
        } else {
          formData.append('file', selectedMedia);
        }

        res = await fetch(`${API_BASE}/whatsapp/send-media`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: formData
        });
      } else {
        res = await fetch(`${API_BASE}/whatsapp/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ to, message: body })
        });
      }

      if (res.ok) {
        setNewMessageText('');
        setSelectedMedia(null);
        // Immediately fetch logs after sending message to show update
        if (activeContact) {
          fetchLogs(activeContact._id);
        }
      } else {
        const data = await res.json();
        alert(data.message || 'Failed to send message via Meta Cloud API.');
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  // Send a template message (starts a new thread or sends in existing thread)
  const handleSendTemplate = async (e) => {
    e.preventDefault();
    if (!newChatPhone || !newChatSelectedTemplate || sendingTemplate) return;

    const bodyComp = newChatSelectedTemplate.components?.find(c => c.type?.toUpperCase() === 'BODY');
    const templateBodyText = bodyComp ? bodyComp.text : '';

    try {
      setSendingTemplate(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/send-template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          to: newChatPhone.trim(),
          templateName: newChatSelectedTemplate.name,
          languageCode: newChatSelectedTemplate.language,
          headerVariables: newChatHeaderVariables,
          bodyVariables: newChatBodyVariables,
          buttonVariables: newChatButtonVariables,
          templateBodyText,
          headerMediaUrl: newChatHeaderMediaUrl
        })
      });

      if (res.ok) {
        const resData = await res.json();
        setShowNewChatModal(false);
        setNewChatPhone('');
        setNewChatSelectedTemplate(null);
        setNewChatTemplateVariables({});
        setNewChatHeaderVariables({});
        setNewChatHeaderMediaUrl('');
        setNewChatBodyVariables({});
        setNewChatButtonVariables({});
        
        // Switch to the proper tab and select active contact
        if (resData.message) {
          const contactPhone = newChatPhone.trim();
          setTimeout(() => {
            setActiveTab('mine');
            // Fetch contacts again and set active contact
            fetch(`${API_BASE}/whatsapp/contacts/mine`, {
              headers: { Authorization: `Bearer ${token}` }
            })
              .then(r => r.json())
              .then(data => {
                setContacts(data);
                const matched = data.find(c => c.phone === contactPhone);
                if (matched) {
                  setActiveContact(matched);
                } else {
                  // Fallback: look in all chats
                  fetch(`${API_BASE}/whatsapp/contacts/all`, {
                    headers: { Authorization: `Bearer ${token}` }
                  })
                    .then(r => r.json())
                    .then(allData => {
                      const matchedAll = allData.find(c => c.phone === contactPhone);
                      if (matchedAll) setActiveContact(matchedAll);
                    });
                }
              });
          }, 800);
        }
        alert('Template message sent successfully!');
      } else {
        const data = await res.json();
        const detailMsg = data.details?.error?.message || (typeof data.details === 'string' ? data.details : JSON.stringify(data.details));
        alert(`${data.message || 'Failed to send template message.'}\n\nDetails: ${detailMsg}`);
      }
    } catch (err) {
      console.error('Error sending template message:', err);
      alert('Error sending template message. Please try again.');
    } finally {
      setSendingTemplate(false);
    }
  };

  // Submit new template to Meta for approval
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    if (!createTemplateName || !createTemplateBody || createTemplateSaving) return;

    // Validate template name (only lowercase letters, numbers, and underscores)
    const nameRegex = /^[a-z0-9_]+$/;
    if (!nameRegex.test(createTemplateName)) {
      setCreateTemplateError('Template name must contain only lowercase letters, numbers, and underscores (no spaces).');
      return;
    }

    try {
      setCreateTemplateSaving(true);
      setCreateTemplateError('');
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: createTemplateName.trim(),
          category: createTemplateCategory,
          language: createTemplateLanguage,
          bodyText: createTemplateBody,
          headerText: createTemplateHeader || undefined,
          footerText: createTemplateFooter || undefined
        })
      });

      if (res.ok) {
        setShowCreateTemplateModal(false);
        setCreateTemplateName('');
        setCreateTemplateBody('');
        setCreateTemplateHeader('');
        setCreateTemplateFooter('');
        fetchTemplates();
        alert('Template submitted successfully for Meta approval!');
      } else {
        const data = await res.json();
        setCreateTemplateError(data.message || 'Failed to submit template.');
      }
    } catch (err) {
      console.error('Error creating template:', err);
      setCreateTemplateError('Server error. Please try again.');
    } finally {
      setCreateTemplateSaving(false);
    }
  };

  // Disconnect handler
  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect this WhatsApp Business integration?')) return;
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/config`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setConfig(null);
        setContacts([]);
        setActiveContact(null);
        setMessages([]);
        setLogs([]);
        setAccessToken('');
        setPhoneNumberId('');
        setShowSettingsModal(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // 11. Save Custom Name
  const handleSaveName = async () => {
    if (!editNameValue.trim() || !activeContact) return;
    try {
      setSavingName(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/contacts/${activeContact._id}/name`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: editNameValue.trim() })
      });
      
      if (res.ok) {
        const data = await res.json();
        // Update instantly
        setActiveContact(data.contact);
        setContacts(prev => prev.map(c => c._id === data.contact._id ? data.contact : c));
        setEditingName(false);
      } else {
        alert('Failed to save name.');
      }
    } catch (err) {
      console.error('Error saving name', err);
    } finally {
      setSavingName(false);
    }
  };

  // 12. Search Leads for linking
  const handleSearchLeads = async (query) => {
    setLeadSearchQuery(query);
    if (query.trim().length < 2) {
      setLeadSearchResults([]);
      return;
    }
    
    try {
      setSearchingLeads(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/leads?search=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLeadSearchResults(data);
      }
    } catch (err) {
      console.error('Error searching leads', err);
    } finally {
      setSearchingLeads(false);
    }
  };

  // 13. Link Lead to Contact
  const handleLinkLead = async (leadId) => {
    if (!activeContact) return;
    try {
      setLinkingLead(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp/contacts/${activeContact._id}/link-lead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ leadId })
      });
      
      if (res.ok) {
        const data = await res.json();
        setActiveContact(data.contact);
        setContacts(prev => prev.map(c => c._id === data.contact._id ? data.contact : c));
        setShowLinkLeadModal(false);
        setLeadSearchQuery('');
        setLeadSearchResults([]);
      } else {
        const err = await res.json();
        alert(err.message || 'Failed to link lead');
      }
    } catch (err) {
      console.error('Error linking lead', err);
    } finally {
      setLinkingLead(false);
    }
  };

  // Auto Scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial load
  useEffect(() => {
    fetchConfig();
  }, []);

  // Fetch contacts or templates when tab changes
  useEffect(() => {
    if (activeTab === 'templates') {
      fetchTemplates();
    } else {
      fetchContacts();
    }
  }, [activeTab]);

  // Fetch thread when contact switches
  useEffect(() => {
    if (activeContact) {
      fetchMessages(activeContact.phone);
      fetchLogs(activeContact._id);
    }
  }, [activeContact]);

  // Real-time socket event subscription
  useEffect(() => {
    if (!socket) return;

    const handleNewMsg = ({ contact, message }) => {
      // 1. If message belongs to active thread, append it
      if (activeContact && activeContact.phone === message.contactPhone) {
        setMessages(prev => {
          if (message.wamid && prev.some(m => m.wamid === message.wamid)) return prev;
          return [...prev, message];
        });
        // Clear read count
        fetch(`${API_BASE}/whatsapp/messages/${activeContact.phone}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` }
        });
        // Fetch fresh logs
        fetchLogs(activeContact._id);
      }

      // 2. Refresh lists
      fetchContacts();
    };

    const handleStatusUpdate = ({ contactPhone, wamid, status }) => {
      if (activeContact && activeContact.phone === contactPhone) {
        setMessages(prev =>
          prev.map(m => (m.wamid === wamid ? { ...m, status } : m))
        );
      }
    };

    const handleRead = ({ phone }) => {
      setContacts(prev =>
        prev.map(c => (c.phone === phone ? { ...c, unreadCount: 0 } : c))
      );
    };

    const handleChatAssignedEvent = () => {
      fetchContacts();
      if (activeContact) {
        fetchLogs(activeContact._id);
      }
    };

    const handleChatDeletedEvent = ({ contactId }) => {
      if (activeContact && activeContact._id === contactId) {
        setActiveContact(null);
        setMessages([]);
        setLogs([]);
      }
      fetchContacts();
    };

    socket.on('new_message', handleNewMsg);
    socket.on('message_status_update', handleStatusUpdate);
    socket.on('contact_read', handleRead);
    socket.on('chat_assigned', handleChatAssignedEvent);
    socket.on('chat_auto_assigned', handleChatAssignedEvent);
    socket.on('chat_reassigned', handleChatAssignedEvent);
    socket.on('chat_deleted', handleChatDeletedEvent);

    return () => {
      socket.off('new_message', handleNewMsg);
      socket.off('message_status_update', handleStatusUpdate);
      socket.off('contact_read', handleRead);
      socket.off('chat_assigned', handleChatAssignedEvent);
      socket.off('chat_auto_assigned', handleChatAssignedEvent);
      socket.off('chat_reassigned', handleChatAssignedEvent);
      socket.off('chat_deleted', handleChatDeletedEvent);
    };
  }, [socket, activeContact]);

  // Helper render status ticks
  const renderMsgStatus = (status) => {
    switch (status) {
      case 'read':
        return <CheckCheck size={16} color="#34B7F1" />;
      case 'delivered':
        return <CheckCheck size={16} color="var(--color-text-tertiary)" />;
      case 'sent':
        return <Check size={16} color="var(--color-text-tertiary)" />;
      case 'failed':
        return <ShieldAlert size={16} color="#E24B4A" title="Failed to deliver" />;
      default:
        return null;
    }
  };

  // Helper to format actions in activity timeline logs
  const renderLogActionIcon = (action) => {
    switch (action) {
      case 'chat_received':
        return <MessageCircle size={16} color="#10b981" />;
      case 'auto_assigned':
        return <UserCheck size={16} color="#3b82f6" />;
      case 'manually_accepted':
        return <CheckSquare size={16} color="#8b5cf6" />;
      case 'reassigned':
        return <ArrowRightLeft size={16} color="#f59e0b" />;
      case 'message_sent':
        return <Send size={14} color="#64748b" />;
      case 'message_received':
        return <Clock size={14} color="#10b981" />;
      case 'chat_deleted':
        return <Trash2 size={16} color="#ef4444" />;
      default:
        return <History size={16} color="#64748b" />;
    }
  };

  const getWebhookURL = () => {
    const origin = window.location.origin;
    if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
      return `https://[YOUR_NGROK_TUNNEL_URL]/api/whatsapp/webhook`;
    }
    return `${API_BASE}/whatsapp/webhook`;
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden', background: '#f5f7fb' }}>
      
      {/* ── LEFT PANEL: TAB SWITCHER & CONTACTS ────────────────────────────────── */}
      <div style={{
        width: '340px',
        background: 'white',
        borderRight: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header Section */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1e293b' }}>WhatsApp Inbox</h3>
            {config?.isConnected && (
              <span style={{ fontSize: '12px', color: socketConnected ? '#22c55e' : '#f59e0b', fontWeight: '500' }}>
                ● {socketConnected ? 'Live Connection' : 'Connecting...'}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {config?.isConnected && (
              <button 
                onClick={() => {
                  setNewChatPhone('');
                  setNewChatSelectedTemplate(null);
                  setNewChatTemplateVariables({});
                  setNewChatHeaderVariables({});
                  setNewChatHeaderMediaUrl('');
                  setNewChatBodyVariables({});
                  setNewChatButtonVariables({});
                  setShowNewChatModal(true);
                }}
                style={{
                  background: '#22c55e',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#16a34a'}
                onMouseLeave={e => e.currentTarget.style.background = '#22c55e'}
              >
                <Plus size={14} /> New Chat
              </button>
            )}
            <button 
              onClick={() => setShowSettingsModal(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#64748b',
                padding: '6px',
                borderRadius: '6px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <Settings size={20} />
            </button>
          </div>
        </div>

        {/* Custom Tab Bar */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc',
          padding: '4px'
        }}>
          {['unassigned', 'mine', 'all', 'templates'].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setActiveContact(null);
              }}
              style={{
                flex: 1,
                padding: '8px 4px',
                fontSize: '13px',
                fontWeight: activeTab === tab ? '600' : '500',
                color: activeTab === tab ? '#22c55e' : '#64748b',
                background: activeTab === tab ? 'white' : 'transparent',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                boxShadow: activeTab === tab ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              {tab === 'unassigned' && 'Unassigned'}
              {tab === 'mine' && 'My Chats'}
              {tab === 'all' && 'All Chats'}
              {tab === 'templates' && 'Templates'}
            </button>
          ))}
        </div>

        {/* Contacts / Templates Scroller */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {activeTab === 'templates' ? (
            loadingTemplates ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>Loading templates...</div>
            ) : !config?.wabaId ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#ef4444', fontSize: '13px' }}>
                WABA ID is required to use templates. Please go to Settings ⚙️ and configure it.
              </div>
            ) : templates.length === 0 ? (
              <div style={{ padding: '40px 24px', textAlign: 'center', color: '#94a3b8' }}>
                <FileText size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                <div>No templates found</div>
                <div style={{ fontSize: '11px', marginTop: '6px' }}>Templates created on Meta will appear here.</div>
                <button
                  onClick={() => setShowCreateTemplateModal(true)}
                  style={{
                    marginTop: '12px',
                    background: '#22c55e',
                    color: 'white',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Create Template
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '8px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '8px', justifyContent: 'space-between', background: '#f8fafc' }}>
                  <button
                    onClick={() => setShowCreateTemplateModal(true)}
                    style={{
                      background: '#22c55e',
                      color: 'white',
                      border: 'none',
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Plus size={12} /> Create
                  </button>
                  <button
                    onClick={fetchTemplates}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#64748b',
                      fontSize: '11px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <RefreshCw size={12} /> Refresh
                  </button>
                </div>
                {templates.map((tmpl) => {
                  const bodyComp = tmpl.components?.find(c => c.type?.toUpperCase() === 'BODY');
                  const bodyText = bodyComp ? bodyComp.text : '';
                  const isApproved = tmpl.status === 'APPROVED';
                  const isPending = tmpl.status === 'PENDING';
                  return (
                    <div
                      key={tmpl.name}
                      onClick={() => {
                        if (tmpl.status === 'APPROVED') {
                          setNewChatSelectedTemplate(tmpl);
                          setNewChatPhone('');
                          setNewChatTemplateVariables({});
                          setNewChatHeaderVariables({});
                          setNewChatHeaderMediaUrl('');
                          setNewChatBodyVariables({});
                          setNewChatButtonVariables({});
                          setShowNewChatModal(true);
                        } else {
                          alert(`This template status is ${tmpl.status}. Only APPROVED templates can be sent.`);
                        }
                      }}
                      style={{
                        padding: '12px 16px',
                        cursor: tmpl.status === 'APPROVED' ? 'pointer' : 'default',
                        borderBottom: '1px solid #f1f5f9',
                        transition: 'background 0.2s',
                        background: 'white'
                      }}
                      onMouseEnter={e => {
                        if (tmpl.status === 'APPROVED') e.currentTarget.style.background = '#f8fafc';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, fontSize: '13px', color: '#334155', wordBreak: 'break-all', marginRight: '8px' }}>
                          {tmpl.name.replace(/_/g, ' ')}
                        </span>
                        <span style={{
                          fontSize: '10px',
                          fontWeight: '600',
                          padding: '2px 6px',
                          borderRadius: '12px',
                          background: isApproved ? '#e2fbe8' : isPending ? '#fef3c7' : '#fee2e2',
                          color: isApproved ? '#15803d' : isPending ? '#d97706' : '#b91c1c'
                        }}>
                          {tmpl.status}
                        </span>
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                        {tmpl.category} • {tmpl.language}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#64748b',
                        marginTop: '6px',
                        background: '#f8fafc',
                        padding: '6px 8px',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {bodyText}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            loadingContacts && contacts.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>Loading conversations...</div>
            ) : contacts.length === 0 ? (
              <div style={{ padding: '40px 24px', textAlign: 'center', color: '#94a3b8' }}>
                <MessageCircle size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                <div>No chats in this queue</div>
                <div style={{ fontSize: '11px', marginTop: '6px' }}>Incoming WhatsApp messages will appear here.</div>
              </div>
            ) : (
              contacts.map((c) => {
                const isActive = activeContact && activeContact.phone === c.phone;
                return (
                  <div
                    key={c._id || c.phone}
                    onClick={() => setActiveContact(c)}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      background: isActive ? '#f0fdf4' : 'transparent',
                      borderBottom: '1px solid #f1f5f9',
                      borderLeft: isActive ? '3px solid #22c55e' : '3px solid transparent',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => {
                      if (!isActive) e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={e => {
                      if (!isActive) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {/* Contact Avatar Circle */}
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: '#e2fbe8',
                        color: '#15803d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        marginRight: '12px'
                      }}>
                        {c.name ? c.name[0].toUpperCase() : <UserIcon size={16} />}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, fontSize: '14px', color: '#334155', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                            {c.name || `+${c.phone}`}
                          </span>
                          <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                            {c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                          <span style={{ fontSize: '12px', color: '#64748b', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '150px' }}>
                            {c.lastMessagePreview || 'Click to view chat'}
                          </span>
                          {c.unreadCount > 0 && (
                            <div style={{
                              background: '#ef4444',
                              color: 'white',
                              borderRadius: '12px',
                              minWidth: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              padding: '0 6px'
                            }}>
                              {c.unreadCount}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Accept / Assigned details below card */}
                    <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {activeTab === 'unassigned' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAcceptChat(c._id);
                          }}
                          style={{
                            background: '#22c55e',
                            color: 'white',
                            border: 'none',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            marginLeft: '52px'
                          }}
                        >
                          Accept Chat
                        </button>
                      )}
                      {c.assignedToName && (
                        <span style={{
                          fontSize: '11px',
                          color: '#64748b',
                          background: '#f1f5f9',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          marginLeft: '52px',
                          fontWeight: '500'
                        }}>
                          👤 {c.assignedToName}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )
          )}
        </div>
      </div>

      {/* ── CENTER PANEL: CHAT WINDOW ─────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#eae6df' }}>
        {activeContact ? (
          <>
            {/* Chat header */}
            <div style={{
              padding: '12px 24px',
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#e2fbe8',
                  color: '#15803d',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  marginRight: '12px'
                }}>
                  {activeContact.name ? activeContact.name[0].toUpperCase() : <UserIcon size={16} />}
                </div>
                <div>
                  {editingName ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input 
                        type="text" 
                        value={editNameValue} 
                        onChange={(e) => setEditNameValue(e.target.value)}
                        autoFocus
                        style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveName();
                          if (e.key === 'Escape') setEditingName(false);
                        }}
                      />
                      <button 
                        onClick={() => setEditingName(false)} 
                        disabled={savingName}
                        style={{ background: '#f1f5f9', color: '#64748b', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <h4 style={{ margin: 0, fontSize: '15px', color: '#1e293b' }}>{activeContact.name || `+${activeContact.phone}`}</h4>
                      <button 
                        onClick={() => {
                          setEditNameValue(activeContact.name || '');
                          setEditingName(true);
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '2px', display: 'flex' }}
                        title="Edit Name"
                      >
                        <span style={{ fontSize: '12px' }}>✏️</span>
                      </button>
                    </div>
                  )}
                  <span style={{ fontSize: '12px', color: '#64748b' }}>+{activeContact.phone}</span>
                </div>
              </div>

              {/* Assignment Actions & Delete Button */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Accept Button if unassigned */}
                {(!activeContact.assignedTo || activeContact.status === 'unassigned') ? (
                  <button
                    onClick={() => handleAcceptChat(activeContact._id)}
                    style={{
                      background: '#22c55e',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <UserCheck size={16} /> Accept Chat
                  </button>
                ) : (
                  // Reassignment Dropdown (Admins / Managers)
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Assigned:</span>
                    <select
                      value={activeContact.assignedTo || ''}
                      onChange={(e) => handleAssignChat(activeContact._id, e.target.value)}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e1',
                        background: 'white',
                        fontSize: '13px',
                        color: '#334155',
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="" disabled>Select User...</option>
                      {employees.map((emp) => (
                        <option key={emp._id} value={emp._id}>
                          {emp.name} ({emp.role})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Permanent Delete */}
                <button
                  onClick={() => handleDeleteChat(activeContact._id)}
                  style={{
                    background: '#fee2e2',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#ef4444',
                    padding: '8px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s'
                  }}
                  title="Delete Chat Permanently"
                  onMouseEnter={e => e.currentTarget.style.background = '#fca5a5'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fee2e2'}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>

            {/* Messages box */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px',
              backgroundImage: 'radial-gradient(#dfdcd6 10%, transparent 10%)',
              backgroundSize: '20px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {loadingMessages && messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#64748b', background: '#ffffffcc', padding: '8px 16px', borderRadius: '20px', margin: 'auto' }}>
                  Loading chat...
                </div>
              ) : (
                messages.map((m) => {
                  const isOut = m.direction === 'outbound';
                  return (
                    <div
                      key={m._id || m.wamid}
                      style={{
                        alignSelf: isOut ? 'flex-end' : 'flex-start',
                        background: isOut ? (m.sentByName === 'AI Assistant' ? '#f3e8ff' : '#d9fdd3') : 'white',
                        color: '#303030',
                        padding: '8px 12px 6px',
                        borderRadius: '8px',
                        boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
                        maxWidth: '60%',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        wordBreak: 'break-word'
                      }}
                    >
                      {m.mediaUrl && (
                        ['image', 'video'].includes(m.type) ? (
                          <img 
                            src={m.mediaUrl.startsWith('http') ? m.mediaUrl : `http://localhost:5000${m.mediaUrl}`} 
                            alt="Media attachment" 
                            style={{ maxWidth: '100%', borderRadius: '8px', marginBottom: '4px' }} 
                            onError={(e) => {
                              // Fallback for deployed APIs
                              e.target.src = m.mediaUrl.startsWith('http') ? m.mediaUrl : `${API_BASE.replace('/api', '')}${m.mediaUrl}`;
                            }}
                          />
                        ) : (
                          <a 
                            href={m.mediaUrl.startsWith('http') ? m.mediaUrl : `${API_BASE.replace('/api', '')}${m.mediaUrl}`} 
                            target="_blank" rel="noreferrer"
                            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px', background: 'rgba(0,0,0,0.05)', borderRadius: '6px', textDecoration: 'none', color: '#0f172a', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}
                          >
                            <Paperclip size={16} /> Document / Media
                          </a>
                        )
                      )}
                      {m.body && <span style={{ fontSize: '14px', marginRight: '36px' }}>{m.body}</span>}
                      
                      {/* Sender identification for outbound message */}
                      {isOut && m.sentByName && (
                        <span style={{ fontSize: '9px', fontStyle: 'italic', color: '#858585', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {m.sentByName === 'AI Assistant' && <span style={{fontSize:'12px'}} title="AI Auto-Reply">🤖</span>}
                          sent by {m.sentByName}
                        </span>
                      )}

                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '4px',
                        marginTop: '2px',
                        fontSize: '9px',
                        color: '#707070',
                        alignSelf: 'flex-end'
                      }}>
                        <span>{new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {isOut && renderMsgStatus(m.status)}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Send bar */}
            {selectedMedia && (
              <div style={{ padding: '8px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ padding: '6px 12px', background: '#e2e8f0', borderRadius: '4px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <ImageIcon size={14} />
                  <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedMedia.name || selectedMedia.fileName || 'Media'}
                  </span>
                  <button 
                    type="button"
                    onClick={() => setSelectedMedia(null)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#64748b' }}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}
            <form
              onSubmit={handleSendMessage}
              style={{
                padding: '16px 24px',
                background: '#f0f2f5',
                display: 'flex',
                gap: '12px',
                alignItems: 'center'
              }}
            >
              <label
                style={{
                  background: '#e2e8f0',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#64748b',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s',
                  flexShrink: 0
                }}
                title="Attach Media"
              >
                <input 
                  type="file" 
                  style={{ display: 'none' }} 
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedMedia(e.target.files[0]);
                    }
                  }} 
                />
                <Paperclip size={18} />
              </label>
              <LibrarySendButton onClick={() => setShowMediaPicker(true)} />
              {config?.wabaId && (
                <button
                  type="button"
                  onClick={() => {
                    setNewChatPhone(activeContact.phone);
                    setNewChatSelectedTemplate(null);
                    setNewChatTemplateVariables({});
                    setNewChatHeaderVariables({});
                    setNewChatHeaderMediaUrl('');
                    setNewChatBodyVariables({});
                    setNewChatButtonVariables({});
                    setShowNewChatModal(true);
                  }}
                  style={{
                    background: '#e2e8f0',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s',
                    flexShrink: 0
                  }}
                  title="Send Template Message"
                  onMouseEnter={e => e.currentTarget.style.background = '#cbd5e1'}
                  onMouseLeave={e => e.currentTarget.style.background = '#e2e8f0'}
                >
                  <FileText size={18} />
                </button>
              )}
              <QuickReplies onSelect={(text) => setNewMessageText(prev => prev ? `${prev} ${text}` : text)} />
              <input
                type="text"
                value={newMessageText}
                onChange={e => setNewMessageText(e.target.value)}
                placeholder={(!activeContact.assignedTo || activeContact.status === 'unassigned') ? "Accept chat to type a message..." : "Type a message..."}
                disabled={sendingMessage}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  borderRadius: '24px',
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  boxShadow: '0 1px 1px rgba(0,0,0,0.05)'
                }}
              />
              <button
                type="submit"
                disabled={!newMessageText.trim() || sendingMessage}
                style={{
                  background: '#22c55e',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'white',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 4px rgba(34,197,94,0.3)',
                  transition: 'opacity 0.2s',
                  opacity: ((!newMessageText.trim() && !selectedMedia) || sendingMessage) ? 0.6 : 1
                }}
              >
                <Send size={16} style={{ marginLeft: '2px' }} />
              </button>
            </form>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', color: '#64748b' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: '#22c55e20',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#22c55e',
              marginBottom: '20px'
            }}>
              <MessageCircle size={48} />
            </div>
            <h3 style={{ margin: 0, color: '#334155' }}>WhatsApp Business Integration</h3>
            <p style={{ fontSize: '14px', marginTop: '6px', color: '#64748b', textAlign: 'center', maxWidth: '360px' }}>
              Select a conversation from the sidebar to view thread, accept claims, assign or delete.
            </p>
          </div>
        )}
      </div>

      {/* ── RIGHT PANEL: ACTIVITY AUDIT LOG ───────────────────────────────────── */}
      {activeContact && (
        <div style={{
          width: '280px',
          background: 'white',
          borderLeft: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column'
        }}>
        }}>
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <History size={18} color="#64748b" />
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#1e293b' }}>Activity Audit Logs</h3>
          </div>
          
          {/* CRM Link Section */}
          <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            {activeContact.leadRef ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>✅ Linked to Lead</span>
                <a 
                  href={`/lead/${activeContact.leadRef}`} 
                  target="_blank" 
                  rel="noreferrer"
                  style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '600' }}
                >
                  ↗ View Lead
                </a>
              </div>
            ) : (
              <button 
                onClick={() => setShowLinkLeadModal(true)}
                style={{
                  width: '100%',
                  background: 'white',
                  border: '1px solid #cbd5e1',
                  color: '#475569',
                  padding: '8px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                🔗 Link to CRM Lead
              </button>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            {loadingLogs && logs.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#64748b' }}>Loading logs...</div>
            ) : logs.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#94a3b8', textAlign: 'center', marginTop: '20px' }}>No activities recorded.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', position: 'relative' }}>
                {/* Timeline vertical bar */}
                <div style={{
                  position: 'absolute',
                  left: '12px',
                  top: '10px',
                  bottom: '10px',
                  width: '2px',
                  background: '#f1f5f9',
                  zIndex: 0
                }} />

                {logs.map((log) => (
                  <div key={log._id} style={{ display: 'flex', gap: '12px', position: 'relative', zIndex: 1 }}>
                    {/* Circle icon */}
                    <div style={{
                      width: '26px',
                      height: '26px',
                      borderRadius: '50%',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {renderLogActionIcon(log.action)}
                    </div>

                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#475569' }}>
                          {log.action === 'chat_received' && 'Chat Initiated'}
                          {log.action === 'auto_assigned' && 'Auto-Assigned'}
                          {log.action === 'manually_accepted' && 'Chat Claimed'}
                          {log.action === 'reassigned' && 'Chat Transferred'}
                          {log.action === 'message_sent' && 'Agent Replied'}
                          {log.action === 'message_received' && 'Client Replied'}
                        </span>
                        <span style={{ fontSize: '9px', color: '#94a3b8' }}>
                          {new Date(log.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' })} {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#64748b', lineHeight: '1.4' }}>
                        {log.note || `${log.performedByName || 'System'} modified chat state.`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── LINK LEAD MODAL ─────────────────────────────────────────────────── */}
      {showLinkLeadModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', borderRadius: '12px', width: '480px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', padding: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1e293b' }}>Link Contact to CRM Lead</h3>
              <button onClick={() => setShowLinkLeadModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>
            
            <input 
              type="text" 
              placeholder="Search leads by name or phone..." 
              value={leadSearchQuery}
              onChange={(e) => handleSearchLeads(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', marginBottom: '16px' }}
            />
            
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {searchingLeads && <div style={{ textAlign: 'center', padding: '12px', color: '#64748b' }}>Searching...</div>}
              {!searchingLeads && leadSearchQuery.length >= 2 && leadSearchResults.length === 0 && (
                <div style={{ textAlign: 'center', padding: '12px', color: '#64748b' }}>No leads found matching "{leadSearchQuery}"</div>
              )}
              {!searchingLeads && leadSearchResults.map(lead => (
                <div 
                  key={lead._id}
                  onClick={() => !linkingLead && handleLinkLead(lead._id)}
                  style={{
                    padding: '12px', borderBottom: '1px solid #f1f5f9', cursor: linkingLead ? 'not-allowed' : 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.2s'
                  }}
                  onMouseEnter={e => { if (!linkingLead) e.currentTarget.style.background = '#f8fafc' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                >
                  <div>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>{lead.name}</div>
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>📞 {lead.phone}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', color: '#3b82f6', fontWeight: '500' }}>{lead.status}</div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{lead.leadType || 'Uncategorized'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SETTINGS MODAL ─────────────────────────────────────────────────── */}
      {showSettingsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '540px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            padding: '28px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <KeyRound size={20} color="#22c55e" />
                WhatsApp API Setup
              </h3>
              {config?.isConnected && (
                <button
                  onClick={() => setShowSettingsModal(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '18px' }}
                >
                  ✕
                </button>
              )}
            </div>

            <form onSubmit={handleSaveSettings} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  Business Display Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. My Agency Support"
                  value={displayName}
                  onChange={e => setDisplayName(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  Phone Number ID
                </label>
                <input
                  type="text"
                  placeholder="e.g. 10982341498114"
                  value={phoneNumberId}
                  onChange={e => setPhoneNumberId(e.target.value)}
                  required
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  Permanent System User Access Token
                </label>
                <input
                  type="password"
                  placeholder={config?.isConnected ? "••••••••••••••••••••" : "Paste your EAA... token"}
                  value={accessToken}
                  onChange={e => setAccessToken(e.target.value)}
                  required={!config?.isConnected}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  Webhook Verification Token (Secret You Choose)
                </label>
                <input
                  type="text"
                  value={verifyToken}
                  onChange={e => setVerifyToken(e.target.value)}
                  required
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                  WhatsApp Business Account (WABA) ID (Required for templates)
                </label>
                <input
                  type="text"
                  placeholder="e.g. 1058434142144"
                  value={wabaId}
                  onChange={e => setWabaId(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}
                />
              </div>

              <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '11px', color: '#64748b' }}>
                <strong>Webhook Configuration for Meta Dashboard:</strong>
                <div style={{ marginTop: '4px', wordBreak: 'break-all' }}>
                  <strong>Webhook URL:</strong> <code>{getWebhookURL()}</code>
                </div>
                <div style={{ marginTop: '2px' }}>
                  <strong>Verify Token:</strong> <code>{verifyToken}</code>
                </div>
              </div>

              {settingsError && (
                <div style={{ color: '#ef4444', fontSize: '13px', fontWeight: '500' }}>
                  ⚠️ {settingsError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <button
                  type="submit"
                  disabled={settingsSaving}
                  style={{
                    flex: 1,
                    background: '#22c55e',
                    border: 'none',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(34,197,94,0.2)'
                  }}
                >
                  {settingsSaving ? 'Connecting...' : 'Save & Connect'}
                </button>
                {config?.isConnected && (
                  <button
                    type="button"
                    onClick={handleDisconnect}
                    style={{
                      background: '#ef4444',
                      border: 'none',
                      color: 'white',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Disconnect Account
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── NEW CHAT / SEND TEMPLATE MODAL ────────────────────────────────────── */}
      {showNewChatModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '500px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            padding: '28px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <PlusCircle size={20} color="#22c55e" />
                Initiate Chat via Template
              </h3>
              <button
                onClick={() => {
                  setShowNewChatModal(false);
                  setNewChatSelectedTemplate(null);
                  setNewChatTemplateVariables({});
                  setNewChatHeaderVariables({});
                  setNewChatHeaderMediaUrl('');
                  setNewChatBodyVariables({});
                  setNewChatButtonVariables({});
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '18px' }}
              >
                ✕
              </button>
            </div>

            {!config?.wabaId ? (
              <div style={{ color: '#ef4444', fontSize: '14px', textAlign: 'center', padding: '12px' }}>
                ⚠️ WABA ID is required to fetch templates. Please add WABA ID in the WhatsApp Settings first.
              </div>
            ) : (
              <form onSubmit={handleSendTemplate} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                    Recipient Phone Number (with Country Code, e.g. 919876543210)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 919876543210"
                    value={newChatPhone}
                    onChange={e => setNewChatPhone(e.target.value)}
                    required
                    disabled={activeContact && activeContact.phone === newChatPhone}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                    Select Approved Template
                  </label>
                  {templates.filter(t => t.status === 'APPROVED').length === 0 ? (
                    <div style={{ fontSize: '12px', color: '#94a3b8', padding: '8px', border: '1px dashed #cbd5e1', borderRadius: '6px' }}>
                      No approved templates found. Fetching...
                      <button type="button" onClick={fetchTemplates} style={{ marginLeft: '8px', color: '#22c55e', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold' }}>
                        Retry
                      </button>
                    </div>
                  ) : (
                    <select
                      value={newChatSelectedTemplate?.name || ''}
                      onChange={(e) => {
                        const selected = templates.find(t => t.name === e.target.value);
                        setNewChatSelectedTemplate(selected);
                        setNewChatTemplateVariables({});
                        setNewChatHeaderVariables({});
                        setNewChatHeaderMediaUrl('');
                        setNewChatBodyVariables({});
                        setNewChatButtonVariables({});
                      }}
                      required
                      style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', background: 'white', cursor: 'pointer' }}
                    >
                      <option value="" disabled>Select a template...</option>
                      {templates.filter(t => t.status === 'APPROVED').map((tmpl) => (
                        <option key={tmpl.name} value={tmpl.name}>
                          {tmpl.name.replace(/_/g, ' ')} ({tmpl.language})
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {newChatSelectedTemplate && (
                  <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Template Preview:</div>
                    <div style={{ fontSize: '13px', color: '#334155', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                      {newChatSelectedTemplate.components?.find(c => c.type?.toUpperCase() === 'HEADER')?.text && (
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                          {newChatSelectedTemplate.components?.find(c => c.type?.toUpperCase() === 'HEADER')?.text}
                        </div>
                      )}
                      {newChatSelectedTemplate.components?.find(c => c.type?.toUpperCase() === 'BODY')?.text}
                    </div>
                  </div>
                )}

                {newChatSelectedTemplate && ['IMAGE', 'DOCUMENT', 'VIDEO'].includes(newChatSelectedTemplate.components?.find(c => c.type?.toUpperCase() === 'HEADER')?.format?.toUpperCase()) && (
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                      Header Media URL
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                        Provide URL for the {newChatSelectedTemplate.components?.find(c => c.type?.toUpperCase() === 'HEADER')?.format?.toUpperCase()}
                      </label>
                      <input
                        type="url"
                        placeholder={`https://example.com/media`}
                        value={newChatHeaderMediaUrl}
                        onChange={(e) => setNewChatHeaderMediaUrl(e.target.value)}
                        required
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }}
                      />
                    </div>
                  </div>
                )}

                {newChatSelectedTemplate && getHeaderPlaceholders(newChatSelectedTemplate).length > 0 && (
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px' }}>
                      Header Variables
                    </div>
                    {getHeaderPlaceholders(newChatSelectedTemplate).map((num) => (
                      <div key={`header-${num}`} style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                          Header Variable {`{{${num}}}`}
                        </label>
                        <input
                          type="text"
                          placeholder={`Value for header {{${num}}}`}
                          value={newChatHeaderVariables[num] || ''}
                          onChange={(e) => {
                            setNewChatHeaderVariables(prev => ({
                              ...prev,
                              [num]: e.target.value
                            }));
                          }}
                          required
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {newChatSelectedTemplate && getBodyPlaceholders(newChatSelectedTemplate).length > 0 && (
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px', marginTop: '12px' }}>
                      Body Variables
                    </div>
                    {getBodyPlaceholders(newChatSelectedTemplate).map((num) => (
                      <div key={`body-${num}`} style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                          Body Variable {`{{${num}}}`}
                        </label>
                        <input
                          type="text"
                          placeholder={`Value for body {{${num}}}`}
                          value={newChatBodyVariables[num] || ''}
                          onChange={(e) => {
                            setNewChatBodyVariables(prev => ({
                              ...prev,
                              [num]: e.target.value
                            }));
                          }}
                          required
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {newChatSelectedTemplate && getTemplateButtonPlaceholders(newChatSelectedTemplate).length > 0 && (
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '8px', marginTop: '12px' }}>
                      Button URL Suffix Variables
                    </div>
                    {getTemplateButtonPlaceholders(newChatSelectedTemplate).map((btn) => (
                      <div key={btn.index} style={{ marginBottom: '10px' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                          Button "{btn.text}" URL Variable
                        </label>
                        <input
                          type="text"
                          placeholder={`Suffix for ${btn.url}`}
                          value={newChatButtonVariables[btn.index] || ''}
                          onChange={(e) => {
                            setNewChatButtonVariables(prev => ({
                              ...prev,
                              [btn.index]: e.target.value
                            }));
                          }}
                          required
                          style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                  <button
                    type="submit"
                    disabled={sendingTemplate || !newChatSelectedTemplate}
                    style={{
                      flex: 1,
                      background: '#22c55e',
                      border: 'none',
                      color: 'white',
                      padding: '12px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(34,197,94,0.2)',
                      opacity: (sendingTemplate || !newChatSelectedTemplate) ? 0.6 : 1
                    }}
                  >
                    {sendingTemplate ? 'Sending...' : 'Send Template'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewChatModal(false);
                      setNewChatSelectedTemplate(null);
                      setNewChatTemplateVariables({});
                      setNewChatHeaderVariables({});
                      setNewChatHeaderMediaUrl('');
                      setNewChatBodyVariables({});
                      setNewChatButtonVariables({});
                    }}
                    style={{
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      background: 'white',
                      fontWeight: '600',
                      color: '#475569',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── CREATE TEMPLATE MODAL ──────────────────────────────────────────────── */}
      {showCreateTemplateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '540px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            padding: '28px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={20} color="#22c55e" />
                Create Message Template
              </h3>
              <button
                onClick={() => {
                  setShowCreateTemplateModal(false);
                  setCreateTemplateError('');
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '18px' }}
              >
                ✕
              </button>
            </div>

            <div style={{ textAlign: 'center', padding: '20px 0 10px' }}>
              <div style={{ width: '64px', height: '64px', background: '#f0fdf4', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <span style={{ fontSize: '32px' }}>📋</span>
              </div>
              <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6', fontSize: '14px', marginBottom: '24px' }}>
                WhatsApp API templates must be created and approved through <strong>Meta Business Manager</strong> before they appear in your CRM.
              </p>

              {/* Steps */}
              <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '24px', marginBottom: '24px', textAlign: 'left' }}>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '16px' }}>How to Get Templates</div>
                {[
                  { step: '1', text: 'Click "Create on Meta" below to open the official WhatsApp Manager.' },
                  { step: '2', text: 'Build your message template and submit it for Meta\'s review.' },
                  { step: '3', text: 'Approval usually takes a few minutes to a few hours.' },
                  { step: '4', text: 'Return here and click "Refresh" — approved templates will appear!' },
                ].map(({ step, text }) => (
                  <div key={step} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div style={{ minWidth: '24px', height: '24px', borderRadius: '50%', background: '#22c55e', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>{step}</div>
                    <span style={{ color: '#475569', fontSize: '13px', lineHeight: '1.5', paddingTop: '2px' }}>{text}</span>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a
                  href="https://business.facebook.com/wa/manage/message-templates/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    background: '#16a34a', color: 'white', textDecoration: 'none',
                    padding: '12px 24px', borderRadius: '8px', fontWeight: '600', fontSize: '14px'
                  }}
                >
                  🔗 Create Template on Meta
                </a>
                <button
                  onClick={() => {
                    setShowCreateTemplateModal(false);
                    fetchTemplates(); // Try to fetch templates on close
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    background: 'white', color: '#16a34a',
                    border: '1px solid #16a34a',
                    padding: '12px 24px', borderRadius: '8px', fontWeight: '600', fontSize: '14px', cursor: 'pointer'
                  }}
                >
                  ✕ Close
                </button>
              </div>

              {/* Coming Soon Note */}
              <div style={{ marginTop: '24px', padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left' }}>
                <span>⚡</span>
                <span style={{ fontSize: '12px', color: '#92400e' }}>
                  <strong>Coming Soon:</strong> In-app template creation with guided approval workflow.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMediaPicker && (
        <MediaPickerModal
          isOpen={true}
          onClose={() => setShowMediaPicker(false)}
          onSelect={(file) => {
            setSelectedMedia(file);
            setShowMediaPicker(false);
          }}
        />
      )}

    </div>
  );
};

const WhatsApp = () => {
  return (
    <WhatsAppSocketProvider>
      <MainLayout>
        <WhatsAppPageContent />
      </MainLayout>
    </WhatsAppSocketProvider>
  );
};

export default WhatsApp;
